import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import AuthShell from "../components/AuthShell";
import { setPageTitle } from "../lib/format";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => setPageTitle("Log In"), []);
  // A visitor who is ALREADY logged in gets bounced to their landing page — but a
  // user who just authenticated here must not be raced away from the reward route.
  const justAuthed = useRef(false);
  const landingFor = (u) => {
    if (location.state?.from) return location.state.from;
    const role = u?.role || (u?.is_admin ? "admin" : "driver");
    return role === "admin" ? "/admin" : role === "support" ? "/support-console" : "/dashboard";
  };
  useEffect(() => { if (user && !justAuthed.current) navigate(landingFor(user), { replace: true }); }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    justAuthed.current = true;
    try {
      const res = await login(email.trim(), password);
      toast.success("Welcome back");
      // Unrevealed first-booking reward (server-authoritative flag) → reveal route;
      // revealed/redeemed/absent rewards take the normal landing page.
      navigate(res?.show_reward ? "/welcome/reward" : landingFor(res?.user), { replace: true });
    } catch (err) {
      justAuthed.current = false;
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testId="login-page" title="Welcome back" subtitle="Log in to your MyRedPark account."
      footer={
        <div className="space-y-5 text-center">
          <div className="flex items-center gap-4" aria-hidden="true">
            <div className="h-px flex-1 bg-[#1F2328]" /><span className="text-[0.68rem] uppercase tracking-[0.18em] text-zinc-600">or</span><div className="h-px flex-1 bg-[#1F2328]" />
          </div>
          <GoogleAuthButton testId="google-login-btn" text="continue_with" onRewardRoute={() => { justAuthed.current = true; }} />
          <button data-testid="apple-login-btn" onClick={() => toast.info("Sign in with Apple is available in the MyRedPark iOS app. Web support activates once our Apple Services ID is configured for myredpark.com.")}
                  className="btn-secondary w-full opacity-70">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.76-1-1.93.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.5.96 1.39 2.1 2.95 3.6 2.89 1.44-.06 1.99-.93 3.73-.93s2.23.93 3.76.9c1.55-.03 2.54-1.41 3.49-2.81 1.1-1.6 1.55-3.15 1.58-3.23-.04-.02-3.03-1.16-3.05-4.62zM14.16 4.06c.79-.96 1.33-2.3 1.18-3.63-1.14.05-2.53.76-3.35 1.72-.74.86-1.38 2.24-1.21 3.56 1.27.1 2.58-.69 3.38-1.65z"/></svg>
            Continue with Apple
          </button>
          <p className="text-sm text-zinc-500">
            New to MyRedPark? <Link to="/register" data-testid="go-register-link" className="text-[#FF3B45] font-semibold hover:underline">Create an account</Link>
          </p>
          <p className="text-xs text-zinc-600">
            By continuing you agree to our <Link to="/terms" className="underline hover:text-zinc-300">Terms</Link> and <Link to="/privacy" className="underline hover:text-zinc-300">Privacy Policy</Link>.
          </p>
        </div>
      }>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="login-email" className="label-dark">Email</label>
          <input id="login-email" data-testid="login-email-input" type="email" required autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="login-password" className="label-dark !mb-0">Password</label>
            <Link to="/reset-password" data-testid="forgot-password-link" className="text-xs text-zinc-500 hover:text-white transition-colors">Forgot?</Link>
          </div>
          <input id="login-password" data-testid="login-password-input" type="password" required autoComplete="current-password"
                 value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark mt-2" placeholder="••••••••" />
        </div>
        <button type="submit" data-testid="login-submit-btn" disabled={busy} className="btn-primary w-full !h-12">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Log In
        </button>
      </form>
    </AuthShell>
  );
}
